import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useInfiniteQuery } from '@tanstack/react-query';

import { fetchData as defaultFetchData } from 'api/FetchData';

import { iaRa } from 'lib/helpers/utility';

export const useInfiniteFetchData = ({
  queryKey,
  url,
  params = {},
  options = {},
  dependencies = [],
  onSuccess = () => {},
  formatData = (data) => data,
  queryFn,
  isOptions = false
}) => {
  const navigate = useNavigate();

  if (!queryKey) {
    throw new Error('The "queryKey" parameter is required for useInfiniteFetchData.');
  }

  if (!queryFn && !url) {
    throw new Error('Either "fetchData" or "url" parameter is required for useInfiniteFetchData.');
  }

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage } = useInfiniteQuery(
    [queryKey, ...dependencies],
    ({ pageParam = params }) => {
      if (queryFn) {
        return queryFn(navigate, pageParam);
      }
      return defaultFetchData(url, navigate, pageParam);
    },
    {
      getNextPageParam: (lastPage, allPages) => {
        const count = Number(lastPage?.count || 0);
        const totalFetched = allPages.reduce(
          (acc, page) => acc + (isOptions ? page?.options?.length : page?.[queryKey]?.length || 0),
          0
        );

        return totalFetched < count ? { ...params, page: allPages.length + 1 } : undefined;
      },
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      ...options,
      onSuccess
    }
  );

  const flattenedData = useMemo(() => {
    if (isOptions) {
      return data?.pages?.flatMap((page) => page?.options);
    }

    const pages = iaRa(data?.pages);

    let result = pages.flatMap((page) => {
      const keys = queryKey.split('.');
      return keys.reduce((obj, key) => obj?.[key], page);
    });

    // if the flattened data is empty, return the pages (this can be empty if queryKey's are not found in the pages)
    if (result.filter(Boolean).length === 0) {
      result = pages;
    }

    return formatData(result);
  }, [data, queryKey, formatData, isOptions]);

  return {
    data: flattenedData,
    fetchNextPage,
    hasNextPage: !!hasNextPage,
    isLoading,
    isFetchingNextPage
  };
};
